<!--
  Driver — dettaglio.

  Scheda a due colonne, in sola lettura di default: la modifica è esplicita
  (Modifica → Salva/Annulla) invece di un form sempre aperto.

  ZONE OPERATIVE — province prese dal seed ISTAT. L'editor mostra l'albero
  Regione→Provincia e salva con PATCH /driver/:id/provinces, che SOSTITUISCE
  l'insieme: si manda lo stato finale, non le differenze. Le `areas` restano
  visibili per i driver non ancora migrati.
-->
<template>
  <div
    v-loading="loading"
    class="dvd"
  >
    <!-- intestazione -->
    <div class="dvd__head">
      <el-button
        class="dvd__back"
        text
        @click="$router.push('/driver')"
      >
        ‹
      </el-button>
      <span class="dvd__avatar">{{ initials }}</span>
      <div>
        <h1 class="dvd__title">
          {{ fullName }}
          <StatusPill
            :kind="isOperativo(driver) ? 'ok' : 'muted'"
            :label="isOperativo(driver) ? 'Attivo' : 'Non attivo'"
          />
          <span
            v-if="driver.is_jolly"
            class="dvd__jolly"
          >Jolly</span>
        </h1>
        <div class="dvd__sub">
          {{ driver.mail }}
          <template v-if="driver.agency">
            · {{ driver.agency.company_name }}
          </template>
        </div>
      </div>
      <div class="dvd__spacer" />
      <div class="dvd__actions">
        <template v-if="!editing">
          <el-button @click="startEdit">
            Modifica
          </el-button>
          <el-button
            type="danger"
            plain
            @click="confirmDelete"
          >
            Elimina
          </el-button>
        </template>
        <template v-else>
          <el-button @click="editing = false">
            Annulla
          </el-button>
          <el-button
            type="primary"
            :loading="saving"
            @click="save"
          >
            Salva modifiche
          </el-button>
        </template>
      </div>
    </div>

    <div class="dvd__grid">
      <div class="dvd__col">
        <!-- anagrafica -->
        <section class="dvd__card">
          <header class="dvd__cardHead">
            <h3>Dati driver</h3>
          </header>
          <div class="dvd__cardBody">
            <div class="dvd__fields">
              <div
                v-for="f in fields"
                :key="f.key"
                class="dvd__field"
              >
                <span class="dvd__label">{{ f.label }}</span>
                <el-input
                  v-if="editing"
                  v-model="draft[f.key]"
                />
                <span
                  v-else
                  class="dvd__value"
                >{{ driver[f.key] || '—' }}</span>
              </div>

              <div class="dvd__field">
                <span class="dvd__label">Cambio preferito</span>
                <el-select
                  v-if="editing"
                  v-model="draft.transmission"
                  style="width: 100%"
                >
                  <el-option
                    label="Automatico"
                    value="automatic"
                  />
                  <el-option
                    label="Manuale"
                    value="manual"
                  />
                  <el-option
                    label="Entrambi"
                    value="both"
                  />
                </el-select>
                <span
                  v-else
                  class="dvd__value"
                >{{ transmissionLabel(driver.transmission) }}</span>
              </div>

              <div class="dvd__field">
                <span class="dvd__label">Ruolo</span>
                <el-switch
                  v-if="editing"
                  v-model="draft.is_jolly"
                  active-text="Jolly"
                  inactive-text="Driver"
                />
                <span
                  v-else
                  class="dvd__value"
                >{{ driver.is_jolly ? 'Jolly' : 'Driver' }}</span>
              </div>

              <div class="dvd__field">
                <span class="dvd__label">Agenzia</span>
                <el-select
                  v-if="editing"
                  v-model="draft.agencyId"
                  filterable
                  placeholder="Scegli l'agenzia"
                  :loading="agenciesLoading"
                  style="width: 100%"
                >
                  <el-option
                    v-for="a in agencies"
                    :key="a.id"
                    :label="a.company_name"
                    :value="a.id"
                  />
                </el-select>
                <span
                  v-else
                  class="dvd__value"
                >{{ driver.agency?.company_name || '—' }}</span>
                <!-- Le tariffe del COD6 si leggono dall'agenzia al momento del
                     calcolo: chi cambia agenzia deve sapere cosa si muove. -->
                <span
                  v-if="editing && agencyChanged"
                  class="dvd__hint"
                >
                  Le corse dei mesi non ancora liquidati si ricalcolano con le
                  tariffe della nuova agenzia. I mesi liquidati non cambiano.
                </span>
              </div>
            </div>
          </div>
        </section>

        <!-- zone operative -->
        <section class="dvd__card">
          <header class="dvd__cardHead">
            <h3>Zone operative</h3>
            <span class="dvd__count">{{ selectedProvinces.length }} province</span>
            <el-button
              v-if="!editingZones"
              size="small"
              @click="startEditZones"
            >
              Modifica zone
            </el-button>
            <template v-else>
              <el-button
                size="small"
                @click="editingZones = false"
              >
                Annulla
              </el-button>
              <el-button
                size="small"
                type="primary"
                :loading="savingZones"
                @click="saveZones"
              >
                Salva zone
              </el-button>
            </template>
          </header>
          <div class="dvd__cardBody">
            <!-- lettura -->
            <template v-if="!editingZones">
              <div
                v-if="groupedProvinces.length"
                class="dvd__regions"
              >
                <div
                  v-for="g in groupedProvinces"
                  :key="g.region"
                  class="dvd__regionRow"
                >
                  <span class="dvd__regionName">{{ g.region }}</span>
                  <div class="dvd__zones">
                    <span
                      v-for="p in g.provinces"
                      :key="p.id"
                      class="dvd__zone"
                    >{{ p.name }} ({{ p.sigla }})</span>
                  </div>
                </div>
              </div>
              <p
                v-else
                class="dvd__empty"
              >
                Nessuna zona assegnata: il driver non comparirà nelle assegnazioni per provincia.
              </p>

              <!-- zone del modello ad aree, da migrare -->
              <div
                v-if="driver.areas?.length"
                class="dvd__legacy"
              >
                Aree del vecchio modello ancora presenti:
                <b>{{ driver.areas.map((a) => a.area).join(', ') }}</b>.
                Vanno sostituite con le province qui sopra.
              </div>
            </template>

            <!-- modifica: albero regione → province -->
            <template v-else>
              <p class="dvd__hint">
                Seleziona le province in cui il driver opera. Il salvataggio sostituisce
                l'intero elenco.
              </p>
              <div
                v-for="r in regions"
                :key="r.id"
                class="dvd__regionBlock"
              >
                <div class="dvd__regionHead">
                  <span class="dvd__regionName">{{ r.name }}</span>
                  <el-button
                    text
                    size="small"
                    @click="toggleRegion(r)"
                  >
                    {{ allSelected(r) ? 'Deseleziona tutte' : 'Seleziona tutte' }}
                  </el-button>
                </div>
                <el-checkbox-group v-model="draftProvinceIds">
                  <el-checkbox
                    v-for="p in r.provinces"
                    :key="p.id"
                    :label="p.id"
                    class="dvd__check"
                  >
                    {{ p.name }} ({{ p.sigla }})
                  </el-checkbox>
                </el-checkbox-group>
              </div>
            </template>
          </div>
        </section>
      </div>

      <!-- riepilogo -->
      <aside class="dvd__aside">
        <section class="dvd__card">
          <header class="dvd__cardHead">
            <h3>Riepilogo</h3>
          </header>
          <div class="dvd__cardBody">
            <div class="dvd__kv">
              <span>Agenzia</span><b>{{ driver.agency?.company_name || '—' }}</b>
            </div>
            <div class="dvd__kv">
              <span>Ruolo</span><b>{{ driver.is_jolly ? 'Jolly' : 'Driver' }}</b>
            </div>
            <div class="dvd__kv">
              <span>Province</span><b>{{ selectedProvinces.length }}</b>
            </div>
            <div class="dvd__kv">
              <span>Regioni</span><b>{{ groupedProvinces.length }}</b>
            </div>
            <div class="dvd__kv">
              <span>Telefono emergenza</span><b>{{ driver.emergency_telephone_number || '—' }}</b>
            </div>
          </div>
        </section>

        <section class="dvd__card">
          <header class="dvd__cardHead">
            <h3>Dati amministrativi</h3>
          </header>
          <div class="dvd__cardBody">
            <div class="dvd__kv">
              <span>Codice fiscale</span><b class="dvd__mono">{{ driver.CF || '—' }}</b>
            </div>
            <div class="dvd__kv">
              <span>IBAN</span><b class="dvd__mono">{{ driver.iban || '—' }}</b>
            </div>
          </div>
        </section>
      </aside>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import driverAPI from '../../services/driverAPI.js';
import agencyAPI from '../../services/agencyAPI.js';
import StatusPill from '../../components/ui/StatusPill.vue';

const FIELDS = [
  { key: 'name', label: 'Nome' },
  { key: 'surname', label: 'Cognome' },
  { key: 'mail', label: 'Email' },
  { key: 'telephone_number', label: 'Telefono' },
  { key: 'emergency_telephone_number', label: 'Telefono emergenza' },
  { key: 'CF', label: 'Codice fiscale' },
  { key: 'iban', label: 'IBAN' },
];

export default {
  name: 'DriverDetailsPage',
  components: { StatusPill },

  setup() {
    const route = useRoute();
    const router = useRouter();

    const driver = ref({});
    const regions = ref([]);
    const loading = ref(false);
    const saving = ref(false);
    const editing = ref(false);
    const draft = reactive({});
    const agencies = ref([]);
    const agenciesLoading = ref(false);

    const editingZones = ref(false);
    const savingZones = ref(false);
    const draftProvinceIds = ref([]);

    const load = async () => {
      loading.value = true;
      try {
        const { data } = await driverAPI.getDriverDetail(route.params.id);
        driver.value = data || {};
      } catch (e) {
        console.error('Caricamento driver fallito:', e);
        ElMessage({ message: 'Impossibile caricare il driver', type: 'error' });
      } finally {
        loading.value = false;
      }
    };

    const selectedProvinces = computed(() => driver.value.provinces || []);

    // Province raggruppate per regione: leggere 12 sigle in fila non dice nulla,
    // "Lombardia: Milano, Monza" sì.
    const groupedProvinces = computed(() => {
      const map = new Map();
      selectedProvinces.value.forEach((p) => {
        const r = p.region?.name || 'Senza regione';
        if (!map.has(r)) map.set(r, []);
        map.get(r).push(p);
      });
      return [...map.entries()]
        .map(([region, provinces]) => ({ region, provinces }))
        .sort((a, b) => a.region.localeCompare(b.region));
    });

    // ---- anagrafica ----
    const loadAgencies = async () => {
      if (agencies.value.length) return;
      agenciesLoading.value = true;
      try {
        const r = await agencyAPI.getAgencies();
        agencies.value = r.data?.data ?? r.data ?? [];
      } catch (e) {
        console.error('Agenzie non caricate:', e);
        ElMessage({ message: 'Elenco agenzie non disponibile', type: 'error' });
      } finally {
        agenciesLoading.value = false;
      }
    };

    const agencyChanged = computed(
      () => !!draft.agencyId && draft.agencyId !== driver.value.agency?.id
    );

    const startEdit = () => {
      FIELDS.forEach((f) => { draft[f.key] = driver.value[f.key] ?? ''; });
      draft.transmission = driver.value.transmission ?? 'both';
      draft.is_jolly = !!driver.value.is_jolly;
      draft.agencyId = driver.value.agency?.id ?? '';
      editing.value = true;
      loadAgencies();
    };

    const save = async () => {
      saving.value = true;
      try {
        const payload = { id: driver.value.id };
        FIELDS.forEach((f) => {
          if (draft[f.key] !== driver.value[f.key]) payload[f.key] = draft[f.key];
        });
        if (draft.transmission !== driver.value.transmission) {
          payload.transmission = draft.transmission;
        }
        if (draft.is_jolly !== driver.value.is_jolly) payload.is_jolly = draft.is_jolly;
        if (agencyChanged.value) payload.agency = { id: draft.agencyId };

        await driverAPI.updateDriver(payload);
        Object.assign(driver.value, payload);
        // Il nome serve subito a intestazione e riepilogo.
        if (payload.agency) {
          driver.value.agency = agencies.value.find((a) => a.id === draft.agencyId);
        }
        editing.value = false;
        ElMessage({ message: 'Dati driver aggiornati', type: 'success' });
      } catch (e) {
        console.error('Salvataggio driver fallito:', e);
        ElMessage({ message: 'Salvataggio non riuscito', type: 'error' });
      } finally {
        saving.value = false;
      }
    };

    // ---- zone ----
    const startEditZones = async () => {
      if (!regions.value.length) {
        try {
          regions.value = await driverAPI.getRegionsTree();
        } catch (e) {
          console.error('Elenco regioni non disponibile:', e);
          ElMessage({ message: 'Impossibile caricare le province', type: 'error' });
          return;
        }
      }
      draftProvinceIds.value = selectedProvinces.value.map((p) => p.id);
      editingZones.value = true;
    };

    const allSelected = (region) =>
      (region.provinces || []).length > 0 &&
      region.provinces.every((p) => draftProvinceIds.value.includes(p.id));

    const toggleRegion = (region) => {
      const ids = (region.provinces || []).map((p) => p.id);
      draftProvinceIds.value = allSelected(region)
        ? draftProvinceIds.value.filter((id) => !ids.includes(id))
        : [...new Set([...draftProvinceIds.value, ...ids])];
    };

    const saveZones = async () => {
      savingZones.value = true;
      try {
        // L'endpoint sostituisce l'insieme: si manda lo stato finale.
        await driverAPI.setDriverProvinces(driver.value.id, draftProvinceIds.value);
        await load(); // rilegge le province con la regione già agganciata
        editingZones.value = false;
        ElMessage({ message: 'Zone aggiornate', type: 'success' });
      } catch (e) {
        console.error('Salvataggio zone fallito:', e);
        ElMessage({
          message: e?.response?.status === 400
            ? 'Province non valide: ricarica la pagina e riprova'
            : 'Salvataggio zone non riuscito',
          type: 'error',
        });
      } finally {
        savingZones.value = false;
      }
    };

    const confirmDelete = async () => {
      try {
        await ElMessageBox.confirm(
          'Il driver verrà eliminato. Confermi?',
          'Elimina driver',
          { confirmButtonText: 'Elimina', cancelButtonText: 'Annulla', type: 'warning' },
        );
      } catch { return; }
      try {
        await driverAPI.deleteDriver(driver.value.id);
        ElMessage({ message: 'Driver eliminato', type: 'success' });
        router.push('/driver');
      } catch (e) {
        console.error('Eliminazione fallita:', e);
        ElMessage({ message: 'Eliminazione non riuscita', type: 'error' });
      }
    };

    const fullName = computed(
      () => [driver.value.name, driver.value.surname].filter(Boolean).join(' ') || 'Driver',
    );
    const initials = computed(
      () => `${(driver.value.name || '')[0] || ''}${(driver.value.surname || '')[0] || ''}`
        .toUpperCase() || '?',
    );
    const transmissionLabel = (t) =>
      t === 'automatic' ? 'Automatico' : t === 'manual' ? 'Manuale' : t === 'both' ? 'Entrambi' : '—';

    onMounted(load);

    return {
      driver, regions, loading, saving, editing, draft, fields: FIELDS,
      agencies, agenciesLoading, agencyChanged,
      editingZones, savingZones, draftProvinceIds,
      selectedProvinces, groupedProvinces,
      startEdit, save, startEditZones, allSelected, toggleRegion, saveZones,
      confirmDelete, fullName, initials, transmissionLabel,
      isOperativo: driverAPI.isOperativo,
    };
  },
};
</script>

<style scoped>
.dvd { padding: 4px 0 32px; }

.dvd__head { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin-bottom: 18px; }
.dvd__back { font-size: 22px; padding: 0 6px; }
.dvd__avatar {
  width: 44px; height: 44px; flex: none; border-radius: 50%;
  display: grid; place-items: center; background: var(--avatar-bg);
  color: #fff; font-weight: 700; font-size: 15px;
}
.dvd__title {
  margin: 0; font-size: 21px; display: flex; align-items: center;
  gap: 10px; flex-wrap: wrap; color: var(--text-primary);
}
.dvd__jolly {
  font-size: 11px; font-weight: 700; letter-spacing: 0.03em; text-transform: uppercase;
  color: var(--svc-fleet); background: var(--svc-fleet-bg);
  border-radius: 5px; padding: 2px 8px;
}
.dvd__sub { margin-top: 3px; font-size: 12.5px; color: var(--text-tertiary); }
.dvd__spacer { flex: 1; }
.dvd__actions { display: flex; gap: 8px; flex-wrap: wrap; }

.dvd__grid {
  display: grid; grid-template-columns: minmax(0, 1fr) 320px;
  gap: 16px; align-items: start;
}
@media (max-width: 960px) { .dvd__grid { grid-template-columns: 1fr; } }
.dvd__col { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
.dvd__aside { position: sticky; top: 16px; display: flex; flex-direction: column; gap: 16px; }
@media (max-width: 960px) { .dvd__aside { position: static; } }

.dvd__card {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); overflow: hidden;
}
.dvd__cardHead {
  display: flex; align-items: center; gap: 10px; padding: 13px 16px;
  border-bottom: 1px solid var(--border-color); flex-wrap: wrap;
}
.dvd__cardHead h3 { margin: 0; font-size: 14px; color: var(--text-primary); }
.dvd__count { margin-left: auto; font-size: 12px; color: var(--text-tertiary); }
.dvd__cardBody { padding: 16px; }

.dvd__fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px 22px; }
@media (max-width: 560px) { .dvd__fields { grid-template-columns: 1fr; } }
.dvd__label {
  display: block; font-size: 10.5px; font-weight: 700; letter-spacing: 0.05em;
  text-transform: uppercase; color: var(--text-tertiary);
}
.dvd__value { display: block; margin-top: 3px; font-size: 14px; font-weight: 600; color: var(--text-primary); }
.dvd__hint { display: block; margin-top: 6px; font-size: 12px; line-height: 1.45; color: var(--text-secondary); }
.dvd__mono { font-family: var(--mono, monospace); font-size: 12.5px; }

/* zone */
.dvd__regions { display: flex; flex-direction: column; gap: 12px; }
.dvd__regionRow { display: flex; flex-direction: column; gap: 5px; }
.dvd__regionName {
  font-size: 11px; font-weight: 700; letter-spacing: 0.05em;
  text-transform: uppercase; color: var(--text-tertiary);
}
.dvd__zones { display: flex; flex-wrap: wrap; gap: 6px; }
.dvd__zone {
  font-size: 12px; font-weight: 600; color: var(--svc-fleet);
  background: var(--svc-fleet-bg); border-radius: 20px; padding: 2px 10px;
}
.dvd__regionBlock { padding: 10px 0; border-bottom: 1px solid var(--border-color); }
.dvd__regionBlock:last-child { border-bottom: 0; }
.dvd__regionHead {
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px; margin-bottom: 6px;
}
.dvd__check { margin-right: 14px; }
.dvd__hint { margin: 0 0 12px; font-size: 12.5px; color: var(--text-tertiary); }
.dvd__empty { margin: 0; font-size: 13px; color: var(--text-tertiary); }
/* le zone del modello ad aree si devono notare: vanno migrate */
.dvd__legacy {
  margin-top: 14px; padding: 10px 12px; font-size: 12.5px;
  color: var(--state-attn); background: var(--state-attn-bg);
  border: 1px solid var(--state-attn-line); border-radius: var(--radius-md);
}

.dvd__kv {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 9px 0; border-bottom: 1px solid var(--border-color);
  font-size: 13px; color: var(--text-secondary);
}
.dvd__kv:last-child { border-bottom: 0; }
.dvd__kv b { color: var(--text-primary); text-align: right; }
</style>
